import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { formatDuration, formatEventTime } from '@/utils/datetime';
import { decamelizeKeysDeep } from '@/utils/decamelize';

import {
	DEFAULT_EVENT_STYLE,
	EVENT_TYPE_STYLES,
	FALLBACK_MATCH_STEP,
	NESTED_INDENT_PX,
	TONE_CLASS,
} from './AgentEventItem.constants';
import type { AgentEventItemProps } from './AgentEventItem.types';

/**
 * One internal `agent_event` rendered as an action message — the equivalent of
 * WhatsApp's "X changed the subject" line: centred, quiet, in its exact
 * chronological place.
 *
 * It used to be folded into a `Pensamiento · N eventos` block. Folded, nobody
 * opened it; inline, it tells the story of what the bot did between one turn
 * and the next.
 */
export const AgentEventItem = ({ event, defaultOpen = false, depth = 0, groupRoot = false }: AgentEventItemProps) => {
	const { t } = useTranslation('translation', { keyPrefix: 'conversationDetail' });
	const [open, setOpen] = useState(defaultOpen);
	const { icon: Icon, tone } = EVENT_TYPE_STYLES[event.eventType] ?? DEFAULT_EVENT_STYLE;
	// El cliente API camelliza las keys dinámicas del payload; restauramos los
	// identificadores snake_case originales para display y lógica.
	const payload = decamelizeKeysDeep(event.payload) as Record<string, unknown>;
	const usedFallback = payload.match_step === FALLBACK_MATCH_STEP;
	const toneClass = usedFallback ? TONE_CLASS.warning : TONE_CLASS[tone];
	const hasPayload = Object.keys(payload).length > 0;

	const nested = depth > 0;
	// A lone event is a timeline milestone and stays centred. One that heads a
	// group, or hangs off another, is left-aligned: it is the only way a child
	// lands to the right of its own parent instead of before it.
	const leftAligned = nested || groupRoot;

	return (
		<li
			className={`flex flex-col gap-2xs ${leftAligned ? 'items-start' : 'items-center'}`}
			data-testid={`agent-event-${event.id}`}
			data-depth={depth || undefined}
			style={nested ? { paddingLeft: `${depth * NESTED_INDENT_PX}px` } : undefined}
		>
			{/* A root event separates itself with rules on either side: it is a
			    timeline milestone. A nested one does the opposite — it sticks to its
			    parent with a vertical guide and aligns left. Centred and flanked by
			    rules it looked like another milestone, which is exactly what it is
			    not: the supervisor's domains hang off the search. */}
			<div className={`flex w-full items-center gap-xs ${nested ? 'border-l-2 border-grey-50 pl-xs' : ''}`}>
				{!leftAligned && <span className="h-px flex-1 bg-grey-50" aria-hidden />}
				<button
					type="button"
					onClick={() => hasPayload && setOpen((v) => !v)}
					aria-expanded={hasPayload ? open : undefined}
					className={`flex min-w-0 max-w-[80%] items-center gap-2xs rounded-full border border-subtle bg-white px-xs py-3xs transition-colors ${
						hasPayload ? 'cursor-pointer hover:bg-grey-25' : 'cursor-default'
					}`}
				>
					<span className={`inline-flex h-lg w-lg shrink-0 items-center justify-center rounded-full ${toneClass}`}>
						<Icon className="h-[11px] w-[11px]" />
					</span>
					<span className="min-w-0 truncate text-[11px] leading-tight text-subtle">
						{event.summary ?? event.eventType}
					</span>
					{event.durationMs != null && (
						<span className="shrink-0 font-mono text-[10px] leading-none text-subtle tabular-nums">
							{formatDuration(event.durationMs)}
						</span>
					)}
					<span className="shrink-0 font-mono text-[10px] leading-none text-grey-200">{event.eventType}</span>
					<span className="shrink-0 font-mono text-[10px] leading-none text-grey-200 tabular-nums">
						{formatEventTime(event.occurredAt)}
					</span>
				</button>
				{!leftAligned && <span className="h-px flex-1 bg-grey-50" aria-hidden />}
			</div>
			{open && hasPayload && (
				<div
					className={`rounded-md border border-subtle bg-white px-xs py-xs ${
						leftAligned ? 'ml-xs w-full' : 'w-full max-w-[80%]'
					}`}
				>
					<p className="mb-3xs text-[10px] font-medium uppercase tracking-wider text-subtle">
						{t('ACTION_EVENT_PAYLOAD')}
					</p>
					<pre className="overflow-x-auto whitespace-pre-wrap rounded-md bg-grey-25 px-xs py-xs font-mono text-[11px] text-default">
						{JSON.stringify(payload, null, 2)}
					</pre>
				</div>
			)}
		</li>
	);
};
